<template>
  <div
    v-if="slots && slots.length > 0"
    :id="slotName"
    :class="['dn-slot', relativePosition && `dn-slot-position-${relativePosition}`]"
  >
    <dn-component
      v-for="(slot, index) in slots"
      :key="`${slotName}-${index}`"
      v-bind="slot"
    />
  </div>
</template>

<script lang="ts" setup>
const props = defineProps({
  slotName: {
    type: String,
    required: true
  },
  relativePosition: {
    type: Number,
    required: false,
    default: null
  }
});

const slotConfig = inject('slotConfig', ref(null));
const slots = computed(() => {
  const config = unref(slotConfig);
  if (!config) {
    return [];
  }
  return config[props.slotName] || [];
});
</script>

<style lang="scss">
.dn-slot {
  @mixin slot-position-rules($children, $base-row, $layout-row: null, $ab-row: null, $sibling-for: null, $bbbb-row: null) {
    @each $n in $children {
      &:nth-child(#{$n}) {
        --dn-grid-item_grid-row: #{$base-row};

        .layout-abb &,
        .layout-bba & {
          --dn-grid-item_grid-row: #{$layout-row};
        }

        @if $ab-row {
          .layout-ab &,
          .layout-ba & {
            @media only screen and (width >= 980px) {
              --dn-grid-item_grid-row: #{$ab-row};
            }
          }
        }

        @if $bbbb-row {
          .layout-bbbb & {
            --dn-grid-item_grid-row: #{$bbbb-row};
          }
        }

        @if $sibling-for and $sibling-for == $n {
          .dn-slot + & {
            --dn-grid-item_grid-row: #{$layout-row};
          }
        }
      }
    }
  }

  &-position {
    &-1 {
      @include slot-position-rules((3, 4), '2 / 3', '3 / 4', '3 / 4');
      @include slot-position-rules(5, '2 / 3', '3 / 4');
      @include slot-position-rules(6, '3 / 4');
      @include slot-position-rules(8, '3 / 4');
    }

    &-2 {
      @include slot-position-rules(4, '3 / 4', '4 / 5', null, 4);
      @include slot-position-rules(5, '4 / 5', '5 / 6', null, null, '3 / 4');
      @include slot-position-rules((6, 8), '4 / 5');
      @include slot-position-rules((7, 9), '5 / 6');
    }

    &-3 {
      @include slot-position-rules(5, 'auto', null, null, null, '4 / 5');
      @include slot-position-rules((6, 8), '5 / 6');
      @include slot-position-rules((7, 9), '6 / 7');
      @include slot-position-rules(10, '7 / 8');
    }
  }

  .layout-abb &,
  .layout-bba &,
  .layout-ab &,
  .layout-ba & {
    --dn-grid-item_grid-column: 1 / 3;

    @media only screen and (width >= 980px) {
      --dn-grid-item_grid-column: 1 / 4;
    }
  }

  .layout-bb & {
    --dn-grid-item_grid-column: 1 / 3;
  }

  .layout-aa & {
    @media only screen and (width >= 980px) {
      --dn-grid-item_grid-column: 1 / 3;
    }
  }

  .layout-bbb &,
  .layout-bab & {
    @media only screen and (width >= 980px) {
      --dn-grid-item_grid-column: 1 / 4;
    }
  }

  .layout-bbbb & {
    @media only screen and (width >= 980px) {
      --dn-grid-item_grid-column: 1 / 5;
    }
  }

  .layout-bbabb &,
  .layout-bbbabbb & {
    --dn-grid-item_grid-column: 1 / 3;

    @media only screen and (width >= 980px) {
      --dn-grid-item_grid-column: 1 / 5;
      --dn-grid-item_grid-row: auto;
    }
  }

  :is(.layout-abb, .layout-bba, .layout-bb):has(.dn-teaser-card-b\+):not(:has(.dn-image-mobile-top)) & {
    @media only screen and (width < 980px) {
      --dn-grid-item_grid-column: 1 / 6;
    }
  }

  .dn-grid-layout.layout-b:has(> &),
  .dn-grid-layout.layout-a:has(> &),
  .dn-grid-layout.layout-a\+:has(> &) {
    --dn-grid_grid-template: auto / minmax(0, 1fr);
  }

  .layout-bb {
    --dn-grid-item_grid-column: auto;
    --dn-grid-item_grid-row: auto;
  }
}
</style>
